import type { ApolloClient } from "@apollo/client";
import { createAsyncThunk, createSelector, createSlice, current, type PayloadAction } from "@reduxjs/toolkit";
import { isArray } from "lodash";
import { shallowEqual } from "react-redux";
import type { RootState } from "store";
import type { LocalDishType, LocalFridgeDishes } from "types/mainTypes";
import { filterAvailableDishTypeIds, filterAvailableDishTypes, sortDishTypes } from "utils/mealListUtils";
import { setShallowEqual } from "utils/setShallowEqual";
import { getAvailableDishTypeIds, mergeDishTypes } from "utils/utils";
import { captureApolloError } from "../services/external/sentry";
import { type Dish, LightingSegmentModeType, type Price } from "../types";
import { getFridgeId } from "../utils/browser/localStorage";
import { FETCH_DISHTYPE_IDS_AND_PRICES, FETCH_UNKNOWN_DISH_TYPE } from "../utils/gqlOperations";
import { logToSentry } from "../utils/logger";
import { selectAllergens, selectFilters, selectSelectedLanguage } from "./appStateSlice";
import { selectOrder, selectTransactionDishes } from "./transactionSlice";

type DishesSlice = LocalFridgeDishes;

const initialState: DishesSlice = {
	types: [],
	items: [],
	unknownDishTypeIds: [],
};

type FetchUnknownDishTypesPayload = {
	orderDishTypes: Pick<LocalDishType, "id">[];
	apolloClient: ApolloClient<unknown>;
	// Id of the refill pass whose inventory.added triggered this fetch (absent for order/browse
	// fetches). Scopes the fulfill-time reconcile below to the right pass's ledger.
	refillId?: string;
};

// Helper function to validate if a dish type has necessary fields for rendering
const isValidDishType = (dishType: LocalDishType): boolean =>
	Boolean(dishType?.id && dishType.description && dishType.price);

export const fetchUnknownDishTypes = createAsyncThunk(
	"dishes/fetchUnknownDishTypesOnTheFly",
	async ({ orderDishTypes, apolloClient, refillId }: FetchUnknownDishTypesPayload, { getState, rejectWithValue }) => {
		const state = getState() as RootState;
		const currentDishTypeIds = new Set(state.dishes.types?.map((type) => type.id) || []);
		const fridgeId = getFridgeId();

		const unknownDishTypeIds = orderDishTypes
			.filter((dishType) => dishType?.id && !currentDishTypeIds.has(dishType.id))
			.map((dish) => dish.id)
			.filter((id, index, array) => array.indexOf(id) === index); // Remove duplicates

		if (unknownDishTypeIds.length === 0 || !fridgeId) {
			return [];
		}

		try {
			logToSentry(
				`Fetching unknown dish types on the fly: ${unknownDishTypeIds.join(", ")} for fridge: ${fridgeId}`,
				"info",
			);

			const results = await Promise.all(
				unknownDishTypeIds.map((dishTypeId) =>
					apolloClient
						.query({
							query: FETCH_UNKNOWN_DISH_TYPE,
							variables: { fridgeId: fridgeId, dishTypeId: dishTypeId },
							fetchPolicy: "network-only",
						})
						.catch((error) => {
							captureApolloError(error, {
								kind: "Fetch_Unknown_Dish_Type_Error",
								query: FETCH_UNKNOWN_DISH_TYPE.kind,
								variables: { fridgeId: fridgeId, dishTypeId: dishTypeId },
							});
							return null;
						}),
				),
			);

			const fetchedDishTypes = results
				.filter((result) => result?.data?.fridge?.dishes?.type)
				.map((result) => ({ ...result?.data.fridge.dishes.type }));

			logToSentry(`Successfully fetched ${fetchedDishTypes.length} unknown dish types for fridge: ${fridgeId}`, "info");

			// JID-2303: a refill-triggered fetch reconciles against the REQUESTING pass's ledger at
			// fulfill time — an unknown type has no pre-refill stock, so the ledger's `added` is its
			// membership truth, and a slow response computed before a revoke must not resurrect the
			// revoked dish (the revocation diff is one-shot). The gate is the refill ID, not
			// "active": the finish frame can itself carry the last revocation, and the FINISHED pass
			// keeps its final ledger in the slot. Ledger-confirmed dishes keep their fetched objects
			// (position feeds the detail segment map); adds newer than the response materialize as
			// stubs, so the restock worker sees the correct count immediately. Once the requesting
			// pass no longer owns the slot its final ledger is gone — discard the stale payload; the
			// next frame carrying the type re-adds it with a full payload.
			if (refillId != null) {
				const slotAtFulfill = (getState() as RootState).transaction.slot;
				if (slotAtFulfill?.kind !== "refill" || slotAtFulfill.transaction.id !== refillId) {
					return [];
				}
				const addedByTypeId = new Map<string, Dish[]>();
				for (const dish of slotAtFulfill.transaction.inventory?.added ?? []) {
					const typeId = dish.type?.id;
					if (!typeId) {
						continue;
					}
					const dishesForType = addedByTypeId.get(typeId);
					if (dishesForType) {
						dishesForType.push(dish);
					} else {
						addedByTypeId.set(typeId, [dish]);
					}
				}
				return fetchedDishTypes.map((dishType) => {
					const fetchedById = new Map(
						((dishType.dishes ?? []) as Dish[]).map((fetchedDish) => [fetchedDish.id, fetchedDish]),
					);
					const dishes = (addedByTypeId.get(dishType.id) ?? []).map(
						(dish) => fetchedById.get(dish.id) ?? ({ id: dish.id } as Dish),
					);
					return { ...dishType, dishes: dishes };
				});
			}

			return fetchedDishTypes;
		} catch (error) {
			captureApolloError(error as Error, {
				kind: "Fetch_Unknown_Dish_Types_Batch_Error",
				query: FETCH_UNKNOWN_DISH_TYPE.kind,
				variables: { fridgeId: fridgeId, unknownDishTypeIds: unknownDishTypeIds.join(",") },
			});
			return rejectWithValue((error as Error).message);
		}
	},
);

export const refetchDishTypePrices = createAsyncThunk(
	"dishes/refetchDishTypePrices",
	async (apolloClient: ApolloClient<unknown>, { rejectWithValue }) => {
		const fridgeId = getFridgeId();

		if (!fridgeId) {
			return rejectWithValue("No fridge ID found");
		}

		try {
			const result = await apolloClient.query({
				query: FETCH_DISHTYPE_IDS_AND_PRICES,
				variables: { id: fridgeId },
				fetchPolicy: "network-only",
			});

			return result.data?.fridge?.dishes?.types || [];
		} catch (error) {
			captureApolloError(error as Error, {
				kind: "Refetch_Dish_Type_Prices_Error",
				query: FETCH_DISHTYPE_IDS_AND_PRICES.kind,
				variables: { id: fridgeId },
			});
			return rejectWithValue((error as Error).message);
		}
	},
);

export const dishesSlice = createSlice({
	name: "dishes",
	initialState: initialState,
	reducers: {
		setInitialDishTypes: (state, action) => {
			state.types = action.payload;
		},
		setDishTypes: (
			state,
			{ payload }: PayloadAction<{ dishTypes: LocalDishType[] | null; reconcileRemovals?: boolean }>,
		) => {
			// reconcileRemovals distinguishes the TWO frame semantics the BE sends on `dishes.types`:
			//  - FULL SNAPSHOT (default, true) — order-finish / browse: an absent existing type means
			//    its last dish was taken (the backend drops it), so empty it. Emptying (not deleting) lets
			//    `getAvailableDishTypeIds` filter it out of the UI.
			//  - SPARSE DELTA (false) — during an active order the BE streams single changed types; an
			//    absent type means "unchanged", NOT removed. Merge only; emptying absent types here
			//    collapsed availableDishTypeIds to the one delta'd type (Meal List + lighting collapse —
			//    the 1.14.x regression of 4af3a56, which wrongly treated every frame as a snapshot).
			//    Taken dishes stay visible via getAvailableDishTypeIds's order-item branch; the post-order
			//    reconcile runs at finish.
			// Active refills never reach this reducer — their stock comes from applyRefillInventory
			// (JID-2303); while a FINISHED refill still holds the slot this stays merge-only (no stock
			// moves without a transaction).
			// (Sparse lighting echoes — a single type, `dishes: null` — never reach here: DeviceContext routes
			// them to `setDishTypesLighting` via `isLightingEchoFrame`.)
			const { dishTypes: updatedDishTypes, reconcileRemovals = true } = payload;

			if (!state.types) {
				if (updatedDishTypes) {
					state.types = updatedDishTypes;
				}
			} else if (isArray(updatedDishTypes)) {
				const merged = mergeDishTypes(current(state.types), updatedDishTypes);
				if (!reconcileRemovals) {
					state.types = merged;
					return;
				}
				const payloadDishTypeIds = new Set(updatedDishTypes.map((dishType) => dishType.id));
				state.types = merged.map((dishType) =>
					payloadDishTypeIds.has(dishType.id) || !(dishType.dishes && dishType.dishes.length > 0)
						? dishType
						: { ...dishType, dishes: [] },
				);
			} else if (updatedDishTypes) {
				state.types = [...mergeDishTypes(current(state.types), updatedDishTypes)];
			}
		},
		// JID-2303: applies the refill transaction's inventory ledger — the SINGLE stock authority while
		// a refill is active (dishes.types frames feed lighting only then; see applyFridgeFrame routing).
		// The BE lists are NET-cumulative per refill transaction (a netted-out dish drops from its
		// list); applyFridgeFrame folds those revocations into the opposite list before dispatching, so
		// the payload received here never overlaps and is re-applied idempotently on every frame — the
		// no-change path (the common case) preserves references, because memoized selectors must not
		// recompute and the kiosk allocation budget is tight. Removed applies first, deterministically.
		// Types emptied here stay in state (`dishes: []`) — getAvailableDishTypeIds hides them (the
		// established "empty, don't delete" pattern). Added dishes of types unknown to the FE are
		// ignored here: applyFridgeFrame fetches those via fetchUnknownDishTypes, which lands the full
		// type payload including its dishes. Ledger dishes carry { id, type: { id }, position } —
		// position feeds the detail segment map (parseFridgeSegments).
		applyRefillInventory: (state, { payload }: PayloadAction<{ removed: Dish[]; added: Dish[] }>) => {
			if (!state.types || state.types.length === 0) {
				return;
			}
			const removedIds = new Set(payload.removed.map((dish) => dish.id));
			const addedByTypeId = new Map<string, Dish[]>();
			for (const dish of payload.added) {
				const typeId = dish.type?.id;
				if (!typeId) {
					continue;
				}
				const dishesForType = addedByTypeId.get(typeId);
				if (dishesForType) {
					dishesForType.push(dish);
				} else {
					addedByTypeId.set(typeId, [dish]);
				}
			}
			if (removedIds.size === 0 && addedByTypeId.size === 0) {
				return;
			}
			const types = current(state.types);
			let hasChanges = false;
			const nextTypes = types.map((dishType) => {
				const dishes = dishType.dishes ?? [];
				const kept = removedIds.size > 0 ? dishes.filter((dish) => !removedIds.has(dish.id)) : dishes;
				const additions = (addedByTypeId.get(dishType.id) ?? []).filter(
					(added) => !kept.some((dish) => dish.id === added.id),
				);
				if (kept.length === dishes.length && additions.length === 0) {
					return dishType;
				}
				hasChanges = true;
				return {
					...dishType,
					dishes: [...kept, ...additions.map((dish) => ({ id: dish.id, position: dish.position ?? null }) as Dish)],
				};
			});
			if (hasChanges) {
				state.types = nextTypes;
			}
		},
		// JID-2303: the BE does not sync `position` onto the refill inventory ledger entries, but
		// every refill dish frame carries the moved type's COMPLETE current dish list with positions.
		// This back-fills/updates `position` by dish id for the types present in the frame — and does
		// NOTHING else: membership stays ledger-driven (applyRefillInventory), so frame dishes missing
		// from state are never added and state dishes absent from the frame are never removed. Types
		// with `dishes: null` (lighting-echo shape) and types unknown to the FE are ignored.
		// Reference-preserving on no-change (the common case — frames repeat at ~1/s).
		syncDishPositions: (state, { payload }: PayloadAction<{ dishTypes: LocalDishType[] }>) => {
			if (!state.types || state.types.length === 0) {
				return;
			}
			const positionsByTypeId = new Map<string, Map<string, Dish["position"]>>();
			for (const frameType of payload.dishTypes) {
				if (frameType?.id && isArray(frameType.dishes) && frameType.dishes.length > 0) {
					positionsByTypeId.set(frameType.id, new Map(frameType.dishes.map((dish) => [dish.id, dish.position])));
				}
			}
			if (positionsByTypeId.size === 0) {
				return;
			}
			const types = current(state.types);
			let hasChanges = false;
			const nextTypes = types.map((dishType) => {
				const framePositions = positionsByTypeId.get(dishType.id);
				if (!(framePositions && dishType.dishes) || dishType.dishes.length === 0) {
					return dishType;
				}
				let typeChanged = false;
				const dishes = dishType.dishes.map((dish) => {
					const framePosition = framePositions.get(dish.id);
					if (framePosition != null && dish.position !== framePosition) {
						typeChanged = true;
						return { ...dish, position: framePosition };
					}
					return dish;
				});
				if (!typeChanged) {
					return dishType;
				}
				hasChanges = true;
				return { ...dishType, dishes: dishes };
			});
			if (hasChanges) {
				state.types = nextTypes;
			}
		},
		// Lighting-only echo from the subscription (the BE reflects a master's spotlight TOGGLE as a
		// SPARSE single-type frame: only the changed type, lighting.mode set, `dishes: null`). It must
		// update per-type lighting.mode ONLY — never the type set or any `dishes` array. Routing it here
		// (instead of `setDishTypes`) is what stops the destructive snapshot reconcile from emptying every
		// other still-stocked type just because the echo omits them (Meal List wipe on detail-open).
		setDishTypesLighting: (state, { payload }: PayloadAction<{ dishTypes: LocalDishType[] }>) => {
			if (!state.types) {
				return;
			}
			const modeById = new Map(
				payload.dishTypes
					.filter((dishType) => dishType?.id && dishType.lighting?.mode != null)
					.map((dishType) => [dishType.id, dishType.lighting?.mode] as const),
			);
			if (modeById.size === 0) {
				return;
			}
			state.types = state.types.map((dishType) => {
				const mode = modeById.get(dishType.id);
				return mode != null && dishType.lighting?.mode !== mode
					? { ...dishType, lighting: { ...dishType.lighting, mode: mode } }
					: dishType;
			});
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchUnknownDishTypes.fulfilled, (state, action: { payload: LocalDishType[] }) => {
				const fetchedDishTypes = action.payload;

				if (fetchedDishTypes.length > 0) {
					// Filter out incomplete dish types
					// that don't have necessary fields for rendering
					const validDishTypes = fetchedDishTypes.filter(isValidDishType);

					if (validDishTypes.length > 0) {
						// Add only the valid fetched dish types to the existing types
						if (state.types) {
							state.types = [...mergeDishTypes(current(state.types), [...validDishTypes])];
						} else {
							state.types = [...validDishTypes];
						}

						// Clear the unknown dish type IDs
						// that were successfully fetched and validated
						const validFetchedIds = validDishTypes.map((dishType) => dishType.id);
						state.unknownDishTypeIds = state.unknownDishTypeIds.filter((id) => !validFetchedIds.includes(id));

						// Log successful validation
						logToSentry(
							`Added ${validDishTypes.length} valid dish types out of ${fetchedDishTypes.length} fetched`,
							"info",
						);
					}

					// Log if some dish types were filtered out due to incomplete data
					if (validDishTypes.length < fetchedDishTypes.length) {
						const invalidCount = fetchedDishTypes.length - validDishTypes.length;
						logToSentry(
							`Filtered out ${invalidCount} incomplete dish types that lack necessary fields for rendering`,
							"warning",
						);
					}
				}
			})
			.addCase(fetchUnknownDishTypes.rejected, (_state, action) => {
				logToSentry("Failed to fetch unknown dish types on the fly", "error", [
					{ payload: JSON.stringify(action.payload) },
				]);
			})
			.addCase(
				refetchDishTypePrices.fulfilled,
				(state, action: PayloadAction<{ id: string; price: Price }[] | null>) => {
					const updatedPrices = action.payload;
					if (state.types && updatedPrices != null && updatedPrices.length > 0) {
						const priceMap = new Map(updatedPrices.map((priceData) => [priceData.id, priceData.price]));

						state.types.forEach((dishType, index) => {
							const updatedPrice = priceMap.get(dishType.id);
							if (updatedPrice && state.types) {
								state.types[index].price = updatedPrice;
							}
						});
					}
				},
			)
			.addCase(refetchDishTypePrices.rejected, (_state, action) => {
				logToSentry("Failed to refetch dish type prices", "error", [{ payload: JSON.stringify(action.payload) }]);
			});
	},
});

export const { applyRefillInventory, setDishTypes, setDishTypesLighting, setInitialDishTypes, syncDishPositions } =
	dishesSlice.actions;

export const selectDishTypes = createSelector([(state: RootState) => state.dishes.types], (dishTypes) => {
	// We want to return only dishes with price and description
	// that secures that we have the most important data for all the displayed dish types
	return dishTypes?.filter((dishType) => isValidDishType(dishType)) || [];
});

export const selectAvailableDishTypesIds = createSelector(
	selectDishTypes,
	selectOrder,
	selectTransactionDishes,
	(fridgeDishTypes, order, orderDishes) => {
		if (!fridgeDishTypes) {
			return new Set<string>();
		}
		return getAvailableDishTypeIds(fridgeDishTypes, orderDishes, order?.state);
	},
	{
		memoizeOptions: {
			resultEqualityCheck: setShallowEqual,
		},
	},
);
export const selectIsFridgeEmpty = createSelector(selectDishTypes, selectOrder, (fridgeDishTypes, order) => {
	const hasDishTypeWithDishes =
		fridgeDishTypes.length > 0 && fridgeDishTypes.some((dishType) => (dishType.dishes?.length ?? 0) > 0);

	return !(order || hasDishTypeWithDishes);
});
export const selectAvailableDishTypes = createSelector(
	selectDishTypes,
	selectAvailableDishTypesIds,
	selectFilters,
	selectSelectedLanguage,
	(fridgeDishTypes, availableDishTypeIds, filters, selectedLanguage) => {
		const filteredDishes = filterAvailableDishTypes(fridgeDishTypes, availableDishTypeIds, filters.allergens, true);
		return sortDishTypes(filteredDishes, selectedLanguage);
	},
);
export const selectDishTypesNotIncludedInFilters = createSelector(
	selectDishTypes,
	selectAvailableDishTypesIds,
	selectFilters,
	selectSelectedLanguage,
	(fridgeDishTypes, availableDishTypeIds, filters, selectedLanguage) => {
		const filteredDishes = filterAvailableDishTypes(fridgeDishTypes, availableDishTypeIds, filters.allergens, false);
		return sortDishTypes(filteredDishes, selectedLanguage);
	},
);

export const selectFilteredDishTypeIds = createSelector(
	[selectDishTypes, selectAvailableDishTypesIds, selectAllergens],
	(fridgeDishTypes, availableDishTypeIds, allergens) =>
		filterAvailableDishTypeIds(fridgeDishTypes, availableDishTypeIds, allergens),
	{
		memoizeOptions: {
			resultEqualityCheck: shallowEqual,
		},
	},
);

export const selectActualSpotlightDishTypeIds = createSelector(
	[selectDishTypes],
	(dishTypes): readonly string[] | null => {
		// Cold-start signal: until at least one dish carries a non-null
		// lighting.mode, the BE hasn't echoed the per-dish lighting state.
		// Return `null` to mark "unknown" so callers (convergence hook) can
		// distinguish from "echoed empty" (`[]`). Covers both empty-dishes
		// and dishes-without-echo cases (`some` on `[]` is `false`).
		const hasAnyEcho = dishTypes.some((dishType) => dishType.lighting?.mode != null);
		if (!hasAnyEcho) {
			return null;
		}
		return dishTypes
			.filter((dishType) => dishType.lighting?.mode === LightingSegmentModeType.Spotlight)
			.map((dishType) => dishType.id);
	},
	{
		memoizeOptions: {
			resultEqualityCheck: shallowEqual,
		},
	},
);

export default dishesSlice.reducer;
